import * as React from 'react';
import { connect } from 'react-redux';
import isNil from 'lodash/isNil';

import CloudinaryVideo from 'components/CloudinaryVideo';
import ConsentPlaceholder from 'components/consent-placeholder/ConsentPlaceholder';

import { isImageUrlByFileExtension, isValidAssetUrl, isValidEntertainmentVideoS3Url, isValidOutfitS3VideoUrl } from 'utils/url';
import { getCloudinaryImageUrl } from 'utils/cloudinary';
import { CHANNEL_TYPE_INSTAGRAM, CHANNEL_TYPE_TIKTOK, CHANNEL_TYPE_YOUTUBE } from 'utils/channels';

import type { GalleryModalItem } from 'types';
import type { ConsentReducerConsentsMap } from 'redux-store/types/ConsentReducer.type';
import type { CloudinaryVideoMapping } from 'redux-store/types/CloudinaryVideosReducer.type';

type OwnProps = {
  selectedItem: GalleryModalItem,
  currentPosition: number
};

type StateProps = {
  userChannelConsents: ConsentReducerConsentsMap,
  videoMapping: Record<string, CloudinaryVideoMapping>
};

type Props = OwnProps & StateProps;

class GalleryModalAsset extends React.PureComponent<Props> {
  // causes X-XSS-Protection error in dev tools. however, it's a known bug in Chrome.
  // https://bugs.chromium.org/p/chromium/issues/detail?id=807304
  // https://stackoverflow.com/a/48745051
  renderYoutube = (): JSX.Element => {
    const { selectedItem, userChannelConsents } = this.props;

    if (!userChannelConsents[CHANNEL_TYPE_YOUTUBE]) {
      return (
        <ConsentPlaceholder
          type="youtube"
          withLearnMore
          link={selectedItem.url}
        />
      );
    }

    return (
      <iframe
        key={`gallery-modal-asset__item-${selectedItem.assetUrl}`}
        className="gallery-modal-asset__item-video"
        src={selectedItem.embedUrl ?? undefined}
        allow="autoplay; encrypted-media"
        allowFullScreen
      />
    );
  };

  private renderCloudinaryVideoItem(cloudinaryVideoId: string | undefined): JSX.Element {
    return (
      <div
        key={`gallery-modal-asset__item-${String(cloudinaryVideoId)}`}
        className="gallery-modal-asset__item-video"
      >
        <CloudinaryVideo
          publicId={cloudinaryVideoId}
          width={816}
          height={460}
          controls
        />
      </div>
    );
  }

  renderCollabaryVideo = (): React.ReactNode => {
    const { selectedItem, videoMapping } = this.props;

    const mappedCloudinaryUrl = !isNil(selectedItem.url) && videoMapping[selectedItem.url];

    if (mappedCloudinaryUrl) {
      return this.renderCloudinaryVideoItem(mappedCloudinaryUrl.publicId);
    }

    if (isImageUrlByFileExtension(selectedItem.url)) {
      return this.renderEmpty();
    }

    return (
      <div
        key={`gallery-modal-asset__item-${String(selectedItem.cloudinaryVideoId)}`}
        className="gallery-modal-asset__item-video"
      >
        {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
        <video
          controls
          controlsList="nodownload"
          src={selectedItem.url}
        />
      </div>
    );
  };

  renderTiktokVideo(): React.ReactNode {
    const { selectedItem, userChannelConsents } = this.props;

    if (!userChannelConsents[CHANNEL_TYPE_TIKTOK]) {
      return (
        <ConsentPlaceholder
          type="tiktok"
          withLearnMore
          link={selectedItem.url}
        />
      );
    }

    return (
      <div className="gallery-modal-asset__external-embed">
        <iframe
          key={`gallery-modal-asset__item-${selectedItem.assetUrl}`}
          className="gallery-modal-asset__item-video gallery-modal-asset__item-video--is-tiktok"
          src={selectedItem.embedUrl ?? undefined}
          allow="autoplay; encrypted-media"
          allowFullScreen
        />
      </div>
    );
  }

  private renderIGReel(): React.ReactNode | undefined {
    const { selectedItem, userChannelConsents } = this.props;

    if (!userChannelConsents[CHANNEL_TYPE_INSTAGRAM]) {
      return (
        <ConsentPlaceholder
          type="instagram"
          withLearnMore
          link={selectedItem.url}
        />
      );
    }

    const reelId = selectedItem.url?.match(/\/reel\/([\w-]+)/)?.[1];
    if (!reelId) {
      return undefined;
    }

    return (
      <div className="gallery-modal-asset__external-embed">
        <iframe
          key={`gallery-modal-asset__item-${selectedItem.assetUrl}`}
          className="gallery-modal-asset__item-video gallery-modal-asset__item-video--is-reel"
          src={`/static/files/ig-reel-wrapper.html?igId=${reelId}`}
          allow="autoplay; encrypted-media"
          allowFullScreen
          scrolling="no"
        />
      </div>
    );
  }

  renderEmpty(): React.ReactNode {
    return (
      <section className="gallery-modal-asset">
        <div className="gallery-modal-asset__item-image" />
      </section>
    );
  }

  render(): React.ReactNode {
    const { selectedItem, currentPosition } = this.props;

    if (
      selectedItem.type === 'collabary-video'
      && (isValidOutfitS3VideoUrl(selectedItem?.url ?? '') || isValidEntertainmentVideoS3Url(selectedItem?.url ?? ''))
    ) {
      return this.renderCollabaryVideo();
    }

    if (!isValidAssetUrl(selectedItem.assetUrl)) {
      return this.renderEmpty();
    }

    if (selectedItem.type === CHANNEL_TYPE_INSTAGRAM && selectedItem.url?.includes('/reel/')) {
      const reelAsset = this.renderIGReel();
      if (reelAsset) {
        // skip further if can't get reel asset
        return reelAsset;
      }
    }

    if (selectedItem.type === CHANNEL_TYPE_YOUTUBE) {
      return this.renderYoutube();
    }

    if (selectedItem.type === 'cloudinary-video') {
      return this.renderCloudinaryVideoItem(selectedItem.cloudinaryVideoId);
    }

    if (selectedItem.type === 'collabary-video') {
      return this.renderCollabaryVideo();
    }

    if (selectedItem.type === CHANNEL_TYPE_TIKTOK) {
      return this.renderTiktokVideo();
    }

    let assetUrl = selectedItem.assetUrl;
    assetUrl = (assetUrl || '').replace(/\?size=[t|m|l]/g, '?size=l');

    if (selectedItem.type === 'cloudinary-image' || selectedItem.type === 'cloudinary-pdf') {
      assetUrl = getCloudinaryImageUrl({ url: assetUrl }, { width: 816, height: 460, preserveAspectRatio: true });
    } else if (selectedItem.type === 'collabary-image') {
      assetUrl = selectedItem.url;
    }

    return (
      <section className="gallery-modal-asset">
        <a
          key={`gallery-modal-asset__item-${assetUrl}--${currentPosition}`}
          href={selectedItem.url}
          className="gallery-modal-asset__item-image"
          style={{ backgroundImage: `url(${assetUrl})` }}
          rel="noopener noreferrer"
          target="_blank"
          aria-label={selectedItem.url}
        />
      </section>
    );
  }
}

const mapStateToProps = ({ consentReducer, cloudinaryVideos }): StateProps => ({
  userChannelConsents: consentReducer.consents,
  videoMapping: cloudinaryVideos.videoMapping
});

export default connect(mapStateToProps)(GalleryModalAsset);
